import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const RegistrarResoluciones = () => {
  const { idEnte, idResolucion } = useParams();
  const navigate = useNavigate();

  // ✅ Editar SOLO si existe idResolucion
  const isEdit = useMemo(() => Boolean(idResolucion), [idResolucion]);

  const [loading, setLoading] = useState(false);

  const [codResolucionEnte, setCodResolucionEnte] = useState("");
  const [fechaResolucionEnte, setFechaResolucionEnte] = useState("");
  const [tipoResolucionEnte, setTipoResolucionEnte] = useState("");
  const [observacionResolucionEnte, setObservacionResolucionEnte] = useState("");

  const cleanData = useCallback(() => {
    setCodResolucionEnte("");
    setFechaResolucionEnte("");
    setTipoResolucionEnte("");
    setObservacionResolucionEnte("");
  }, []);

  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get(`/api/resolucion/listid/${idResolucion}`);
      if (data?.ok) {
        const r = data.resolucionEnte;

        setCodResolucionEnte(
          r?.codResolucionEnte !== undefined && r?.codResolucionEnte !== null
            ? String(r.codResolucionEnte)
            : ""
        );
        setFechaResolucionEnte(r?.fechaResolucionEnte?.substring(0, 10) ?? "");
        setTipoResolucionEnte(r?.tipoResolucionEnte ?? "");
        setObservacionResolucionEnte(r?.observacionResolucionEnte ?? "");
      } else {
        Swal.fire("No encontrada", "No se encontró la resolución.", "warning");
      }
    } catch (error) {
      console.error("Error al obtener la resolución:", error);
      Swal.fire("Error", "No se pudo cargar la resolución.", "error");
    } finally {
      setLoading(false);
    }
  }, [idResolucion]);

  useEffect(() => {
    if (isEdit) getData();
  }, [isEdit, getData]);

  const saveResolucion = async () => {
    try {
      if (!idEnte) {
        Swal.fire({
          icon: "error",
          title: "Falta el Ente",
          text: "No se recibió el id del EnteConformador en la URL.",
        });
        return;
      }

      // ✅ Validación numérica
      if (!codResolucionEnte || Number.isNaN(Number(codResolucionEnte))) {
        Swal.fire({
          icon: "warning",
          title: "Validación",
          text: "El código de resolución debe ser un número válido.",
        });
        return;
      }

      setLoading(true);

      const newResolucion = {
        codResolucionEnte: Number(codResolucionEnte),
        fechaResolucionEnte,
        tipoResolucionEnte,
        observacionResolucionEnte,
        enteObjectId: idEnte,
      };

      await axios.post("/api/resolucion/add", newResolucion);

      await Swal.fire({
        icon: "success",
        title: "Resolución registrada exitosamente",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData(); // ✅ al guardar
      navigate(-1);
    } catch (error) {
      console.error("Error en saveResolucion:", error);

      const msg =
        error.response?.data?.message ||
        "Ocurrió un error inesperado al guardar la resolución.";

      Swal.fire({
        icon: "error",
        title: "Error al registrar la resolución",
        text: msg,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateResolucion = async () => {
    try {
      // ✅ Validación numérica si el backend admite actualizar código
      if (!codResolucionEnte || Number.isNaN(Number(codResolucionEnte))) {
        Swal.fire({
          icon: "warning",
          title: "Validación",
          text: "El código de resolución debe ser un número válido.",
        });
        return;
      }

      setLoading(true);

      const updatedData = {
        codResolucionEnte: Number(codResolucionEnte),
        fechaResolucionEnte,
        tipoResolucionEnte,
        observacionResolucionEnte,
      };

      await axios.put(`/api/resolucion/update/${idResolucion}`, updatedData);

      await Swal.fire({
        icon: "success",
        title: "Resolución actualizada exitosamente",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData(); // ✅ al actualizar
      navigate(-1);
    } catch (error) {
      console.error("Error al actualizar la resolución:", error);
      Swal.fire({
        icon: "error",
        title: "Error al actualizar la resolución",
        text: error.response?.data?.message || "Ocurrió un error inesperado.",
      });
    } finally {
      setLoading(false);
    }
  };

  const actions = async (e) => {
    e.preventDefault();
    isEdit ? await updateResolucion() : await saveResolucion();
  };

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {isEdit ? "Editar Resolución" : "Registrar Resolución"}
                </h5>
                <small className="text-secondary">
                  Completa la información de la resolución asociada al ente.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                  title="Volver">
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-3 p-md-4">
            {loading && (
              <div className="text-center text-secondary py-3">
                <div className="spinner-border" role="status" />
                <div className="mt-2">Procesando…</div>
              </div>
            )}

            <form id="form-resolucion" onSubmit={actions} noValidate>
              <div className="row g-3">
                {/* Código */}
                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">Código Resolución</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Ej: 1001"
                    min="0"
                    value={codResolucionEnte}
                    onChange={(e) => setCodResolucionEnte(e.target.value)}
                    required
                  // Si NO quieres que se edite el código en edición, activa esto:
                  // disabled={isEdit}
                  />
                </div>

                {/* Fecha */}
                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">Fecha de Resolución</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fechaResolucionEnte}
                    onChange={(e) => setFechaResolucionEnte(e.target.value)}
                  />
                </div>

                {/* Tipo */}
                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">Tipo de Resolución</label>
                  <select
                    className="form-select"
                    value={tipoResolucionEnte}
                    onChange={(e) => setTipoResolucionEnte(e.target.value)}
                  >
                    <option value="">Seleccione…</option>
                    <option value="Tecnica">Técnica</option>
                    <option value="Academica">Académica</option>
                  </select>
                </div>

                {/* Observación */}
                <div className="col-12 col-md-12">
                  <label className="form-label fw-semibold">Observación</label>
                  <textarea
                    type="text"
                    className="form-control"
                    placeholder="Escriba una observación (opcional)"
                    rows={5}
                    value={observacionResolucionEnte}
                    onChange={(e) => setObservacionResolucionEnte(e.target.value)}
                  />
                </div>

                {/* Footer botones */}
                <div className="col-12 pt-2">
                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-dark"
                      onClick={cleanData}
                      disabled={loading}
                      title="Limpiar"
                    >
                      <i className="fa-solid fa-eraser me-2"></i>
                      Limpiar
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => {
                        cleanData();
                        navigate(-1);
                      }}
                      disabled={loading}
                      title="Cancelar"
                    >
                      <i className="fa-solid fa-xmark me-2"></i>
                      Cancelar
                    </button>

                    <button
                      type="submit"
                      form="form-resolucion"
                      className="btn btn-dark"
                      disabled={loading}
                      title={isEdit ? "Actualizar" : "Guardar"}
                    >
                      <i
                        className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                          } me-2`}
                      ></i>
                      {isEdit ? "Actualizar" : "Guardar"}
                    </button>
                  </div>
                </div>
              </div>
            </form>

          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarResoluciones;
